import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import {
  type AccuracyRun,
  type AdminAirline,
  type ManualCheck,
  type RawState,
  type Refusal,
  moment,
  needsAttention,
  topLines,
} from '../../lib/admin-airlines'
import { fmtNum } from '../../lib/format'
import { AirlinesBoard } from './AirlinesBoard'

export type Board = {
  generatedAt: string
  airlines: AdminAirline[]
  checks: ManualCheck[]
  accuracyRun: AccuracyRun | null
}

export interface ViewProps {
  loading: boolean
  signedIn: boolean
  refusal: Refusal | null
  board: Board | null
  all: boolean
  setAll: (v: boolean) => void
  open: string | null
  raw: RawState
  shown: { path: string; text: string } | null
  onToggle: (prefix: string) => void
  onOpenRaw: (path: string) => void
}

/** Everything /admin/airlines shows, from the sign-in check to the open line; the page only feeds it. */
export function AirlinesView(p: ViewProps) {
  return (
    <div className="mx-auto max-w-6xl px-4 pt-10 pb-16 sm:px-5">
      <header className="flex flex-wrap items-end justify-between gap-4 border-b border-border pb-5">
        <div>
          <div className="mono text-[11px] uppercase tracking-wider text-faint">
            Admin · Air cargo
          </div>
          <h1
            className="mt-2 font-display text-3xl text-ink"
            style={{ fontWeight: 600, letterSpacing: '-0.01em' }}
          >
            Airline monitor
          </h1>
          <p className="mt-1.5 max-w-xl text-sm text-muted">
            Customer requests, refusal share and data accuracy for every airline, most requested
            first.
          </p>
        </div>
        {p.board && (
          <span className="mono rounded-full border border-border-strong px-3 py-1 text-xs text-muted">
            Updated {moment(p.board.generatedAt)}
          </span>
        )}
      </header>
      <Body {...p} />
    </div>
  )
}

function Body(p: ViewProps) {
  if (p.loading) return <Skeleton label="Checking your sign-in…" />
  if (!p.signedIn || p.refusal?.kind === 'signed_out') {
    return (
      <Notice tone="muted">
        Sign in with an admin account to see this page.{' '}
        <Link to="/login" className="font-semibold text-signal hover:underline">
          Sign in
        </Link>
      </Notice>
    )
  }
  if (p.refusal?.kind === 'not_admin')
    return <Notice tone="down">This account has no access to the admin pages.</Notice>
  if (p.refusal)
    return (
      <Notice tone="down">
        Could not load the board. <span className="text-muted">{p.refusal.message}</span>
      </Notice>
    )
  if (!p.board) return <Skeleton label="Loading the board…" />
  const lines = topLines(p.board.airlines, p.all)
  return (
    <>
      <Summary lines={lines} airlines={p.board.airlines} />
      <AccuracyBanner run={p.board.accuracyRun} />
      <NeedsAttention airlines={p.board.airlines} onPick={(prefix) => pickLine(p, lines, prefix)} />
      <div className="mt-6 flex flex-wrap items-center justify-between gap-3 text-xs text-muted">
        <p className="max-w-2xl">
          Refusal share turns amber from 20% and red from 50% with 10+ requests in 24h. Click an
          airline for its waybills, accuracy check and raw answers.
        </p>
        <label className="flex cursor-pointer items-center gap-2 text-sm text-ink">
          <input
            type="checkbox"
            className="accent-signal"
            checked={p.all}
            onChange={(e) => p.setAll(e.target.checked)}
          />
          Show all airlines
        </label>
      </div>
      <div className="mt-3 overflow-x-auto rounded-2xl border border-border bg-surface-0 shadow-sm">
        {lines.length === 0 ? (
          <p className="px-5 py-12 text-center text-sm text-muted">
            No customer requests in the last 7 days. Tick “Show all airlines” to see every line.
          </p>
        ) : (
          <AirlinesBoard
            airlines={lines}
            checks={p.board.checks}
            open={p.open}
            raw={p.raw}
            shown={p.shown}
            onToggle={p.onToggle}
            onOpenRaw={p.onOpenRaw}
          />
        )}
      </div>
    </>
  )
}

/** Opens a line from the Needs attention list: shows it if hidden, opens it if closed, scrolls to it. */
function pickLine(p: ViewProps, shown: AdminAirline[], prefix: string) {
  if (!shown.some((a) => a.prefix === prefix)) p.setAll(true)
  if (p.open !== prefix) p.onToggle(prefix)
  setTimeout(
    () => document.getElementById(`line-${prefix}`)?.scrollIntoView({ behavior: 'smooth' }),
    50,
  )
}

const ATTENTION_DOT = { red: 'bg-down', yellow: 'bg-amber-500' } as const

function NeedsAttention(p: { airlines: AdminAirline[]; onPick: (prefix: string) => void }) {
  const list = needsAttention(p.airlines)
  return (
    <section className="mt-4 rounded-2xl border border-border bg-surface-0">
      <h2 className="border-b border-border px-4 py-2.5 text-[11px] font-medium uppercase tracking-wider text-muted">
        Needs attention
        {list.length > 0 && <span className="mono ml-2 text-down">{list.length}</span>}
      </h2>
      {list.length === 0 ? (
        <p className="flex items-center gap-2 px-4 py-3 text-sm text-up">
          <span className="h-2 w-2 rounded-full bg-up" />
          All good: no line is down, refusing customers or failing the accuracy check.
        </p>
      ) : (
        <ul className="divide-y divide-border">
          {list.map(({ airline: a, tone, reasons }) => (
            <li key={a.prefix}>
              <button
                type="button"
                data-attention={a.prefix}
                className="flex w-full items-start gap-3 px-4 py-2.5 text-left text-sm hover:bg-surface-1"
                onClick={() => p.onPick(a.prefix)}
              >
                <span className={`mt-1.5 h-2 w-2 flex-none rounded-full ${ATTENTION_DOT[tone]}`} />
                <span className="min-w-0 flex-1">
                  <span className="font-medium text-ink">{a.name}</span>{' '}
                  <span className="mono text-xs text-faint">{a.prefix}</span>
                  <span className="block text-muted">{reasons.join(' · ')}</span>
                </span>
                <span className="mono flex-none text-xs text-muted tabular-nums">
                  {fmtNum(a.refusals.asked_7d, 0)} req 7d
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

function AccuracyBanner({ run }: { run: AccuracyRun | null }) {
  if (!run) return null
  const [tone, text] = !run.enabled
    ? [
        'border-amber-500/30 bg-amber-500/10 text-amber-800',
        `Accuracy check is off (AIR_ACCURACY_ENABLED)${run.last_at ? `. Last run: ${moment(run.last_at)}` : ''}`,
      ]
    : run.last_at
      ? [
          'border-border bg-surface-1 text-ink',
          `Last accuracy run: ${moment(run.last_at)}, ${run.lines} lines checked`,
        ]
      : ['border-border bg-surface-1 text-muted', 'Accuracy check is on, no run yet']
  return (
    <p className={`mt-4 rounded-xl border px-4 py-2.5 text-sm ${tone}`} data-accuracy-run>
      {text}
    </p>
  )
}

/** Shown lines and their requests; Down and Degraded count every line, shown or not. */
function Summary({ lines, airlines }: { lines: AdminAirline[]; airlines: AdminAirline[] }) {
  const tiles = [
    { label: 'Airlines shown', value: fmtNum(lines.length, 0), tone: 'text-ink' },
    {
      label: 'Requests 7d',
      value: fmtNum(
        lines.reduce((n, a) => n + a.refusals.asked_7d, 0),
        0,
      ),
      tone: 'text-signal',
    },
    {
      label: 'Down, all lines',
      value: fmtNum(airlines.filter((a) => a.state === 'red').length, 0),
      tone: 'text-down',
    },
    {
      label: 'Degraded, all lines',
      value: fmtNum(airlines.filter((a) => a.state === 'yellow').length, 0),
      tone: 'text-amber-700',
    },
  ]
  return (
    <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
      {tiles.map((t) => (
        <div key={t.label} className="rounded-xl border border-border bg-surface-0 px-4 py-3">
          <div className="text-[11px] uppercase tracking-wider text-muted">{t.label}</div>
          <div className={`mono mt-1 text-2xl font-semibold tabular-nums ${t.tone}`}>{t.value}</div>
        </div>
      ))}
    </div>
  )
}

function Notice({ tone, children }: { tone: 'muted' | 'down'; children: ReactNode }) {
  const ink = tone === 'down' ? 'text-down' : 'text-ink'
  return (
    <div className={`mt-6 rounded-2xl border border-border bg-surface-1 px-5 py-4 text-sm ${ink}`}>
      {children}
    </div>
  )
}

function Skeleton({ label }: { label: string }) {
  return (
    <div className="mt-6" aria-busy="true">
      <p className="text-sm text-muted">{label}</p>
      <div className="mt-3 overflow-hidden rounded-2xl border border-border bg-surface-0">
        {[0, 1, 2, 3, 4].map((i) => (
          <div key={i} className="flex items-center gap-4 border-b border-border px-4 py-4">
            <div className="h-3 w-40 animate-pulse rounded bg-surface-2" />
            <div className="h-3 w-16 animate-pulse rounded-full bg-surface-2" />
            <div className="ml-auto h-3 w-12 animate-pulse rounded bg-surface-2" />
          </div>
        ))}
      </div>
    </div>
  )
}
